import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { apiRequest, ApiError } from '../lib/api'
import {
  accountFrom,
  clearSession,
  getDevice,
  readSession,
  storeSession,
} from '../lib/session'
import type { Account, Session } from '../types'

interface AuthPayload {
  account: Account
  token: string
  expiresAt: string
}

interface AuthContextValue {
  account: Account | null
  session: Session | null
  restoring: boolean
  serviceUnavailable: boolean
  createAccountStart: (email: string, passcode: string) => Promise<{ expiresInSeconds?: number }>
  createAccountVerify: (email: string, code: string) => Promise<void>
  signIn: (email: string, passcode: string) => Promise<void>
  forgotPasscodeStart: (email: string) => Promise<void>
  resetPasscode: (email: string, code: string, newPasscode: string) => Promise<void>
  changePasscode: (currentPasscode: string, newPasscode: string) => Promise<boolean>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

function requireAuthPayload(value: AuthPayload): AuthPayload {
  const account = accountFrom(value.account)
  if (!account || typeof value.token !== 'string' || typeof value.expiresAt !== 'string') {
    throw new Error('The service returned an incomplete sign-in response.')
  }
  return { ...value, account }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(() => readSession())
  const [restoring, setRestoring] = useState(() => Boolean(readSession()))
  const [serviceUnavailable, setServiceUnavailable] = useState(false)

  const finishAuthentication = useCallback((raw: AuthPayload) => {
    const data = requireAuthPayload(raw)
    const next: Session = { account: data.account, token: data.token, expiresAt: data.expiresAt }
    storeSession(next)
    setSession(next)
    setServiceUnavailable(false)
  }, [])

  useEffect(() => {
    if (!session) {
      setRestoring(false)
      return
    }
    let active = true
    void apiRequest<{ account?: Account }>('get_account', {}, { token: session.token })
      .then(({ data, meta }) => {
        if (!active) return
        const account = accountFrom(data.account ?? data)
        if (!account) throw new Error('The account response was incomplete.')
        const next = {
          account,
          token: session.token,
          expiresAt: meta.session?.expiresAt ?? session.expiresAt,
        }
        storeSession(next)
        setSession(next)
        setServiceUnavailable(false)
      })
      .catch((error) => {
        if (!active) return
        if (error instanceof ApiError && (error.code === 'UNAUTHORIZED' || error.code === 'SESSION_EXPIRED')) {
          clearSession()
          setSession(null)
        } else {
          setServiceUnavailable(true)
        }
      })
      .finally(() => active && setRestoring(false))
    return () => {
      active = false
    }
  }, [])

  const createAccountStart = useCallback(async (email: string, passcode: string) => {
    const result = await apiRequest<{ expiresInSeconds?: number }>('create_account_start', { email, passcode })
    return result.data
  }, [])

  const createAccountVerify = useCallback(async (email: string, code: string) => {
    const device = getDevice()
    const result = await apiRequest<AuthPayload>('create_account_verify', {
      email,
      code,
      deviceId: device.deviceId,
      deviceName: device.deviceName,
    })
    finishAuthentication(result.data)
  }, [finishAuthentication])

  const signIn = useCallback(async (email: string, passcode: string) => {
    const device = getDevice()
    const result = await apiRequest<AuthPayload>('sign_in', {
      email,
      passcode,
      deviceId: device.deviceId,
      deviceName: device.deviceName,
    })
    finishAuthentication(result.data)
  }, [finishAuthentication])

  const forgotPasscodeStart = useCallback(async (email: string) => {
    await apiRequest('forgot_passcode_start', { email })
  }, [])

  const resetPasscode = useCallback(async (email: string, code: string, newPasscode: string) => {
    await apiRequest('reset_passcode', { email, code, newPasscode })
    clearSession()
    setSession(null)
  }, [])

  const changePasscode = useCallback(async (currentPasscode: string, newPasscode: string) => {
    if (!session) throw new Error('Please sign in again.')
    const result = await apiRequest<{ sessionRemainsValid?: boolean }>(
      'change_passcode',
      { currentPasscode, newPasscode },
      { token: session.token },
    )
    if (!result.data.sessionRemainsValid) {
      clearSession()
      setSession(null)
      return false
    }
    return true
  }, [session])

  const signOut = useCallback(async () => {
    const token = session?.token
    try {
      if (token) await apiRequest('sign_out', {}, { token })
    } finally {
      clearSession()
      setSession(null)
    }
  }, [session])

  const value = useMemo<AuthContextValue>(() => ({
    account: session?.account ?? null,
    session,
    restoring,
    serviceUnavailable,
    createAccountStart,
    createAccountVerify,
    signIn,
    forgotPasscodeStart,
    resetPasscode,
    changePasscode,
    signOut,
  }), [
    session,
    restoring,
    serviceUnavailable,
    createAccountStart,
    createAccountVerify,
    signIn,
    forgotPasscodeStart,
    resetPasscode,
    changePasscode,
    signOut,
  ])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext)
  if (!value) throw new Error('useAuth must be used inside AuthProvider.')
  return value
}
